<template>
  <div class="period-field" :class="{ 'period-field--block': block }">
    <v-menu
      ref="menu"
      v-model="open"
      offset-y
      nudge-bottom="4"
      min-width="auto"
      :disabled="disabled"
      :close-on-content-click="false"
      content-class="app-menu app-menu--picker"
      role="none"
    >
      <template v-slot:activator="{ on, attrs }">
        <app-button
          kind="field"
          icon="calendar"
          chevron
          :block="block"
          :disabled="disabled"
          :aria-label="'Zeitraum: ' + label"
          aria-haspopup="dialog"
          v-bind="attrs"
          v-on="on"
        >{{ label }}</app-button>
      </template>
      <div
        ref="panel"
        class="period-field__panel"
        role="dialog"
        aria-label="Zeitraum wählen"
        tabindex="-1"
        @keydown.tab="trapTab"
      >
        <div v-if="!monthsOnly && !daysOnly" class="period-field__mode">
          <app-segment v-model="draftType" :items="modes" label="Zeitraum nach" />
        </div>
        <!-- Schnellwahl (z. B. Fahrerabrechnung): übernimmt sofort -->
        <div v-if="presets.length" class="period-field__presets" role="group" aria-label="Schnellwahl">
          <app-button v-for="preset in presets" :key="preset.text" @click="applyPreset(preset)">{{ preset.text }}</app-button>
        </div>
        <v-date-picker
          v-if="draftType === 'monthly'"
          key="monate"
          v-model="draftMonths"
          type="month"
          range
          no-title
          locale="de"
          color="primary"
          :max="max || undefined"
        ></v-date-picker>
        <v-date-picker
          v-else
          key="tage"
          v-model="draftDays"
          range
          no-title
          locale="de"
          color="primary"
          :first-day-of-week="1"
        ></v-date-picker>
        <div class="period-field__actions">
          <app-button @click="open = false">Abbrechen</app-button>
          <!-- weiß wie „Abbrechen“ (Kundenwunsch 12.10.2026: „nicht farbig sondern auch weis“) -->
          <app-button :disabled="!canApply" @click="apply">Übernehmen</app-button>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script>
// Zeitraum-Feld (Spezifikation 4.2.3 Nr. 1): Feldknopf mit Kalender und ⌄, Menü mit „Monat | Tag“, Bereichs-Datumspicker
// und „Abbrechen“/„Übernehmen“ (beide weiß, seit 12.10.2026). Beschriftung „Oktober 2026“, „Sep.–Okt. 2026“,
// „Dez. 2025–Jan. 2026“ (Monate) bzw. „01.10.–08.10.2026“, „28.12.2025–03.01.2026“ (Tage). monthsOnly (Exporte,
// Citybus & Flexibus): nur Monatsbereich, ausgeschriebene Monate („August–September 2026“). daysOnly (Fahrerabrechnung):
// nur Tagesbereich; presets: Schnellwahl-Knöpfe über dem Datumspicker, die sofort übernehmen.
// Werte wie auf den Seiten: periodType 'monthly' | 'daily', Monate 'YYYY-MM', Tage 'YYYY-MM-DD'.
// Ereignis apply ({ periodType, from, to }) nach „Übernehmen“, sortiert; ein einzelner Klick ergibt von = bis.
// Die Wurzel ist ein eigenes <div> wie bei AppSelect: Vuetify verschiebt den Aktivator neben das (unsichtbare)
// v-menu-Element, er muss deshalb in einem eigenen Behälter liegen.
// Tastatur (seit der Prüfung vom 12.10.2026, Fokusführung aus panelMenu.js wie Filter und Gruppierung): beim Öffnen
// Fokus ins Panel („Monat | Tag“, sonst der Datumspicker), Tab und Umschalt+Tab bleiben im Panel, Esc schließt und
// gibt den Fokus an den Feldknopf (VMenu). Vorher blieb der Fokus am Knopf und Tab lief am offenen Panel vorbei.
import moment from 'moment';
import { raiseMenu } from '@/components/ui/AppSelect.vue';
import { formatMonth, LEER } from '@/utils/format';
import panelMenu from './panelMenu';

const MODES = [
  { value: 'monthly', text: 'Monat' },
  { value: 'daily', text: 'Tag' }
];

function parse(value, format) {
  const m = value ? moment(value, format, true) : null;
  return m && m.isValid() ? m.locale('de') : null;
}

// „Oktober 2026“; Bereich kurz „Sep.–Okt. 2026“ / „Dez. 2025–Jan. 2026“, lang „August–September 2026“
function monthRange(from, to, long) {
  const a = parse(from, 'YYYY-MM');
  const b = parse(to, 'YYYY-MM');
  if (!a || !b) {
    return a || b ? formatMonth(a ? from : to) : LEER;
  }
  if (a.isSame(b, 'month')) {
    return formatMonth(from);
  }
  const name = long ? 'MMMM' : 'MMM';
  if (a.isSame(b, 'year')) {
    return a.format(name) + '–' + b.format(name + ' YYYY');
  }
  return a.format(name + ' YYYY') + '–' + b.format(name + ' YYYY');
}

// „08.10.2026“; Bereich „01.10.–08.10.2026“ / „28.12.2025–03.01.2026“
function dayRange(from, to) {
  const a = parse(from, 'YYYY-MM-DD');
  const b = parse(to, 'YYYY-MM-DD');
  if (!a || !b) {
    const one = a || b;
    return one ? one.format('DD.MM.YYYY') : LEER;
  }
  if (a.isSame(b, 'day')) {
    return a.format('DD.MM.YYYY');
  }
  if (a.isSame(b, 'year')) {
    return a.format('DD.MM.') + '–' + b.format('DD.MM.YYYY');
  }
  return a.format('DD.MM.YYYY') + '–' + b.format('DD.MM.YYYY');
}

function range(from, to) {
  return [from, to].filter(Boolean);
}

export default {
  name: 'PeriodField',
  // Fokus beim Öffnen und Tab im Panel
  mixins: [panelMenu],
  props: {
    periodType: { type: String, default: 'monthly' },
    fromMonth: { type: String, default: null },
    month: { type: String, default: null },
    fromDate: { type: String, default: null },
    date: { type: String, default: null },
    // nur Monatsbereich (ohne Umschalter), ausgeschriebene Monatsnamen
    monthsOnly: { type: Boolean, default: false },
    // nur Tagesbereich (ohne Umschalter; Fahrerabrechnung, Kundenwunsch 13.10.2026)
    daysOnly: { type: Boolean, default: false },
    // Schnellwahl über dem Datumspicker: [{ text, from, to }] mit Tagen 'YYYY-MM-DD'; ein Klick übernimmt sofort
    presets: { type: Array, default: () => [] },
    // spätester wählbarer Monat 'YYYY-MM' (Exporte: laufender Monat)
    max: { type: String, default: null },
    block: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false }
  },
  data() {
    return {
      open: false,
      draftType: 'monthly',
      draftMonths: [],
      draftDays: [],
      modes: MODES
    };
  },
  computed: {
    daily() {
      return this.daysOnly || (!this.monthsOnly && this.periodType === 'daily');
    },
    label() {
      return this.daily
        ? dayRange(this.fromDate, this.date)
        : monthRange(this.fromMonth, this.month, this.monthsOnly);
    },
    canApply() {
      const list = this.draftType === 'monthly' ? this.draftMonths : this.draftDays;
      return Array.isArray(list) && list.length > 0;
    }
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        // Entwurf bei jedem Öffnen aus den Werten der Seite
        this.draftType = this.daily ? 'daily' : 'monthly';
        this.draftMonths = range(this.fromMonth, this.month);
        this.draftDays = range(this.fromDate, this.date);
        this.focusWhenShown(12);
      }
    }
  },
  mounted() {
    raiseMenu(this.$refs.menu);
  },
  methods: {
    applyPreset(preset) {
      this.open = false;
      this.$emit('apply', { periodType: 'daily', from: preset.from, to: preset.to });
    },
    apply() {
      const list = (this.draftType === 'monthly' ? this.draftMonths : this.draftDays).slice().sort();
      if (!list.length) {
        return;
      }
      this.open = false;
      this.$emit('apply', {
        periodType: this.draftType,
        from: list[0],
        to: list[list.length - 1]
      });
    }
  }
};
</script>

<style scoped>
.period-field {
  display: inline-flex;
  flex: none;
  max-width: 100%;
  min-width: 0;
  vertical-align: middle;
}
.period-field--block {
  display: flex;
  width: 100%;
}
.period-field__panel {
  display: flex;
  flex-direction: column;
}
/* Fokus am Panel selbst nur nach einem Klick auf eine freie Stelle (Esc und Tab wirken weiter): ohne Rahmen */
.period-field__panel:focus {
  outline: none;
}
.period-field__mode {
  padding: 12px 12px 4px;
}
.period-field__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 12px 12px 4px;
}
.period-field__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 12px 12px;
  border-top: 1px solid var(--line-2);
}
</style>
